import { useQuery } from '@tanstack/react-query'
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router'
import { BarChart3, Building2, ClipboardCheck, Landmark, Scale, HandCoins, Home, ReceiptText, UserCircle, Users } from 'lucide-react'
import { canWriteDepartment, describeRoles, homeFor } from '@/auth/profile'
import { useAuth, useProfile } from '@/auth/SessionProvider'
import { listDepartments } from '@/data/departments'
import { getChurchSettings } from '@/data/admin'
import { listWaitingBudgets } from '@/data/budgets'
import { listWaitingExpenses } from '@/data/expenses'
import { AppShell } from './AppShell'
import { Breadcrumb } from './Breadcrumb'
import type { Crumb } from './Breadcrumb'
import { UserMenu } from './UserMenu'
import { useMediaQuery } from './useMediaQuery'
import { useNavStyle } from './useNavStyle'
import type { NavItem } from './MobileShell'

const TITLES: [RegExp, string][] = [
  [/^\/account/, 'Account'],
  [/^\/admin\/approvals/, 'Approvals'],
  [/^\/admin\/reconcile/, 'Reconcile'],
  [/^\/admin\/income/, 'Church income'],
  [/^\/admin\/deletions/, 'Deletion log'],
  [/^\/admin\/history/, 'History'],
  [/^\/admin\/reports/, 'Church summary'],
  [/^\/admin\/settings/, 'Settings'],
  [/^\/admin\/people/, 'People'],
  [/^\/admin/, 'Church overview'],
  [/^\/d\/[^/]+\/members\/import(\/|$)/, 'Import members'],
  [/^\/d\/[^/]+\/members\/[^/]+/, 'Member'],
  [/^\/d\/[^/]+\/members/, 'Members'],
  [/^\/d\/[^/]+\/pay(\/|$)/, 'Record payment'],
  [/^\/d\/[^/]+\/payments/, 'Payments'],
  [/^\/d\/[^/]+\/dues/, 'Dues plans'],
  [/^\/d\/[^/]+\/arrears/, 'Who is behind'],
  [/^\/d\/[^/]+\/budgets/, 'Budgets'],
  [/^\/d\/[^/]+\/expenses/, 'Expenses'],
  [/^\/d\/[^/]+\/remittances/, 'Money sent'],
  [/^\/d\/[^/]+\/sheets/, 'Collection sheets'],
  [/^\/d\/[^/]+\/report/, 'Reports'],
  [/^\/d\/[^/]+\/projects/, 'Projects'],
  [/^\/d\/[^/]+\/history/, 'History'],
  [/^\/d\//, 'Department'],
]
const titleFor = (pathname: string) => TITLES.find(([re]) => re.test(pathname))?.[1] ?? 'Church Accounts'

/** The signed-in frame: navigation that fits the screen, and a sign-out button. */
export function AppLayout() {
  const profile = useProfile()
  const { signOut } = useAuth()
  const router = useRouter()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  // Which department are we "in"? The one in the address, else the person's own.
  const fromUrl = /^\/d\/([^/]+)/.exec(pathname)?.[1]
  const home = homeFor(profile)
  const deptId = fromUrl ?? (home && home.to === '/d/$deptId' ? home.params.deptId : undefined)

  // The top bar says WHERE you are (which department), not what the page below already
  // says in its own heading: repeating "Members" over "Members" wasted the space, and
  // an admin moving between departments needs to see which one they are in.
  const depts = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const deptName = fromUrl ? depts.data?.find((d) => d.id === fromUrl)?.name : undefined
  const isCentralDept = !!deptId && depts.data?.find((d) => d.id === deptId)?.is_central === true

  // Whole path segments only: '/pay' must not also light up for '/payments'.
  const isActive = (href: string, exact = false) =>
    exact ? pathname === href : pathname === href || pathname.startsWith(href + '/')
  // No breadcrumb on the page a person lands on: there is nothing above it.
  const isLanding = pathname === '/admin' || (!!deptId && pathname === `/d/${deptId}`)

  // The church name under the logo: every signed-in person may read it (RLS setting_read).
  const settings = useQuery({ queryKey: ['church-settings'], queryFn: getChurchSettings, staleTime: 5 * 60_000 })

  // The count on Approvals, same queries (and cache) as the Home page.
  const staff = profile.isChurchReader && !deptId
  const waitingBudgets = useQuery({ queryKey: ['waiting-budgets'], queryFn: listWaitingBudgets, enabled: staff })
  const waitingExpenses = useQuery({ queryKey: ['waiting-expenses'], queryFn: listWaitingExpenses, enabled: staff })
  const waitingCount = (waitingBudgets.data?.length ?? 0) + (waitingExpenses.data?.length ?? 0)

  const navItems: NavItem[] = []

  if (deptId) {
    const base = `/d/${deptId}`
    // Church-wide readers get a way back out of a department.
    if (profile.isChurchReader) {
      navItems.push({ label: 'Church', href: '/admin', icon: Building2, active: false })
    } else {
      navItems.push({ label: 'Home', href: base, icon: Home, active: isActive(base, true) })
    }
    // The central treasury collects no dues, so it gets none of the dues tabs.
    if (!isCentralDept) {
      navItems.push({ label: 'Members', href: `${base}/members`, icon: Users, active: isActive(`${base}/members`) })
      if (canWriteDepartment(profile, deptId)) {
        navItems.push({ label: 'Pay', href: `${base}/pay`, icon: HandCoins, active: isActive(`${base}/pay`) })
      }
      navItems.push({ label: 'Payments', href: `${base}/payments`, icon: ReceiptText, active: isActive(`${base}/payments`) })
    }
  } else {
    navItems.push({ label: 'Home', href: '/admin', icon: Home, active: isActive('/admin', true) })
    navItems.push({ label: 'Approvals', href: '/admin/approvals', icon: ClipboardCheck, active: isActive('/admin/approvals'), group: 'Daily work', badge: waitingCount })
    navItems.push({ label: 'Reconcile', href: '/admin/reconcile', icon: Scale, active: isActive('/admin/reconcile'), group: 'Daily work' })
    navItems.push({ label: 'Income', href: '/admin/income', icon: Landmark, active: isActive('/admin/income'), group: 'Daily work' })
    navItems.push({ label: 'Reports', href: '/admin/reports', icon: BarChart3, active: isActive('/admin/reports'), group: 'Insights' })
  }
  navItems.push({ label: 'Account', href: '/account', icon: UserCircle, active: isActive('/account'), pinned: true })

  // Where you are: Home / Approvals, or Church / Youth Ministry / Payments.
  const pageTitle = titleFor(pathname)
  const crumbs: Crumb[] = []
  if (!isLanding) {
    if (!deptId) crumbs.push({ label: 'Home', href: '/admin' }, { label: pageTitle })
    else {
      // The department's name is the page title just below, so the trail starts from where you came in.
      crumbs.push(profile.isChurchReader ? { label: 'Church', href: '/admin' } : { label: 'Home', href: `/d/${deptId}` })
      crumbs.push({ label: pageTitle })
    }
  }
  const isPhone = !useMediaQuery('(min-width: 768px)')
  const [navStyle, setNavStyle] = useNavStyle()
  const go = (href: string) => void router.navigate({ to: href })
  const roleText = describeRoles(profile, (id) => depts.data?.find((d) => d.id === id)?.name)[0] ?? ''

  return (
    <AppShell
      // Church-wide pages carry their own heading; the top bar only repeats a title where it adds information.
      title={deptName && !isLanding ? deptName : isLanding ? settings.data?.churchName ?? 'Church overview' : undefined}
      navItems={navItems}
      onNavigate={go}
      churchName={settings.data?.churchName}
      user={{ name: profile.fullName, role: roleText }}
      breadcrumb={<Breadcrumb crumbs={crumbs} onNavigate={go} />}
      actions={
        <UserMenu
          name={profile.fullName}
          email={profile.email}
          onAccount={() => go('/account')}
          onSignOut={() => void signOut()}
          drawerOption={isPhone ? { on: navStyle === 'drawer', onChange: (on) => setNavStyle(on ? 'drawer' : 'tabs') } : undefined}
        />
      }
    >
      <Outlet />
    </AppShell>
  )
}
